import React from 'react';
import {
    Box,
    Typography,
    Paper,
    Stack,
    Grid,
    Switch,
    Slider,
    FormControlLabel,
    TextField,
    InputAdornment,
    Tabs,
    Tab,
    Tooltip,
    Button,
    Select,
    MenuItem,
    SelectChangeEvent
} from '@mui/material';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import TabletIcon from '@mui/icons-material/Tablet';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import { CouponDesign, Breakpoint, GridSettings } from '../../../types/coupon';
import { ColorPicker } from './ColorPicker';
import AssetSelectionModal from './AssetSelectionModal';

// Get base URL for images from environment or default
const BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';

interface GlobalSettingsProps {
    design: CouponDesign;
    gridSettings: GridSettings;
    onDesignUpdate: (design: CouponDesign) => void;
    onGridSettingsUpdate: (settings: GridSettings) => void;
}

export const GlobalSettings: React.FC<GlobalSettingsProps> = ({
    design,
    gridSettings,
    onDesignUpdate,
    onGridSettingsUpdate
}) => {
    const [activeBreakpoint, setActiveBreakpoint] = React.useState<Breakpoint>('desktop');
    const [isAssetModalOpen, setIsAssetModalOpen] = React.useState(false);

    const handleBreakpointChange = (_: React.SyntheticEvent, newValue: Breakpoint) => {
        setActiveBreakpoint(newValue);
    };

    const handleCanvasUpdate = (property: keyof typeof design.canvas) => (value: any) => {
        onDesignUpdate({
            ...design,
            canvas: {
                ...design.canvas,
                [property]: value
            }
        });
    };

    const handleBreakpointSettingChange = (property: 'width' | 'height') => (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = Number(event.target.value);
        if (isNaN(value)) return;

        const updatedDesign: CouponDesign = {
            ...design,
            canvas: {
                ...design.canvas,
                breakpoints: {
                    ...design.canvas.breakpoints,
                    [activeBreakpoint]: {
                        ...design.canvas.breakpoints[activeBreakpoint],
                        [property]: value
                    }
                }
            }
        };

        onDesignUpdate(updatedDesign);
    };

    const handleBackgroundImageSelect = (asset: any) => {
        // Calculate scaling ratios for each breakpoint based on current desktop dimensions
        const desktopRatio = design.canvas.breakpoints.desktop.width / design.canvas.breakpoints.desktop.height;
        const tabletRatio = design.canvas.breakpoints.tablet.width / design.canvas.breakpoints.tablet.height;
        const mobileRatio = design.canvas.breakpoints.mobile.width / design.canvas.breakpoints.mobile.height;

        // Get image dimensions
        const imageWidth = asset.dimensions.width;
        const imageHeight = asset.dimensions.height;

        // Calculate new dimensions for each breakpoint maintaining their original aspect ratios
        const newBreakpoints = {
            desktop: {
                width: imageWidth,
                height: imageHeight
            },
            tablet: {
                width: Math.round(imageWidth * 0.75), // Tablet is typically 75% of desktop
                height: Math.round((imageWidth * 0.75) / tabletRatio)
            },
            mobile: {
                width: Math.round(imageWidth * 0.5), // Mobile is typically 50% of desktop
                height: Math.round((imageWidth * 0.5) / mobileRatio)
            }
        };

        onDesignUpdate({
            ...design,
            canvas: {
                ...design.canvas,
                background: {
                    url: asset.url.replace(/^\/+/, ''), // Remove leading slashes
                    fit: design.canvas.background?.fit || 'cover',
                    originalName: asset.originalName
                },
                breakpoints: newBreakpoints
            }
        });
        setIsAssetModalOpen(false);
    };

    const getImageUrl = (path: string) => {
        const cleanPath = path.replace(/^\/+/, '');
        const url = `${BASE_URL}/${cleanPath}`;
        return process.env.NODE_ENV === 'development' ? `${url}?t=${new Date().getTime()}` : url;
    };

    const handleBackgroundFitChange = (event: SelectChangeEvent<'cover' | 'contain' | 'stretch'>) => {
        const fit = event.target.value as 'cover' | 'contain' | 'stretch';
        if (!design.canvas.background) return;
        
        onDesignUpdate({
            ...design,
            canvas: {
                ...design.canvas,
                background: {
                    ...design.canvas.background,
                    fit
                }
            }
        });
    };

    const handleRemoveBackground = () => {
        onDesignUpdate({
            ...design,
            canvas: {
                ...design.canvas,
                background: undefined
            }
        });
    };

    return (
        <Stack spacing={3}>
            {/* Canvas Background */}
            <Paper 
                elevation={0} 
                sx={{ 
                    p: 2,
                    bgcolor: '#f5f5f5',
                    border: '1px solid',
                    borderColor: '#e0e0e0'
                }}
            >
                <Typography 
                    variant="subtitle2" 
                    gutterBottom
                    sx={{ 
                        color: '#1976d2',
                        fontWeight: 600
                    }}
                >
                    Canvas Background
                </Typography>
                <Stack spacing={2}>
                    <ColorPicker
                        label="Background Color"
                        value={design.canvas.backgroundColor}
                        onChange={(color) => handleCanvasUpdate('backgroundColor')(color)}
                    />
                    <Box>
                        <Button 
                            variant="outlined" 
                            onClick={() => setIsAssetModalOpen(true)}
                            fullWidth
                            sx={{ mb: 1 }}
                        >
                            {design.canvas.background ? 'Change Background Image' : 'Set Background Image'}
                        </Button>
                        {design.canvas.background && (
                            <>
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: 140,
                                        backgroundColor: '#f5f5f5',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mb: 1,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                        overflow: 'hidden'
                                    }}
                                >
                                    <img
                                        src={getImageUrl(design.canvas.background.url)}
                                        alt={design.canvas.background.originalName || 'Background'}
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '100%',
                                            objectFit: 'contain'
                                        }}
                                    />
                                </Box>
                                <Select
                                    size="small"
                                    fullWidth
                                    value={design.canvas.background.fit}
                                    onChange={handleBackgroundFitChange}
                                    sx={{ mb: 1 }}
                                >
                                    <MenuItem value="cover">Cover</MenuItem>
                                    <MenuItem value="contain">Contain</MenuItem>
                                    <MenuItem value="stretch">Stretch</MenuItem>
                                </Select>
                                <Button 
                                    variant="outlined" 
                                    color="error"
                                    onClick={handleRemoveBackground}
                                    fullWidth
                                >
                                    Remove Background Image
                                </Button>
                            </>
                        )}
                    </Box>
                </Stack>
            </Paper>

            {/* Canvas Dimensions */}
            <Paper 
                elevation={0} 
                sx={{ 
                    p: 2,
                    bgcolor: '#f5f5f5',
                    border: '1px solid',
                    borderColor: '#e0e0e0'
                }}
            >
                <Typography 
                    variant="subtitle2" 
                    gutterBottom
                    sx={{ 
                        color: '#1976d2',
                        fontWeight: 600
                    }}
                >
                    Canvas Dimensions
                </Typography>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                    <Tabs
                        value={activeBreakpoint}
                        onChange={handleBreakpointChange}
                        variant="fullWidth"
                        sx={{
                            minHeight: 36,
                            '& .MuiTab-root': {
                                minHeight: 36,
                                minWidth: 48
                            }
                        }}
                    >
                        <Tab 
                            icon={<Tooltip title="Desktop"><DesktopWindowsIcon /></Tooltip>} 
                            value="desktop" 
                            sx={{ p: 1 }}
                        />
                        <Tab 
                            icon={<Tooltip title="Tablet"><TabletIcon /></Tooltip>} 
                            value="tablet"
                            sx={{ p: 1 }}
                        />
                        <Tab 
                            icon={<Tooltip title="Mobile"><SmartphoneIcon /></Tooltip>} 
                            value="mobile"
                            sx={{ p: 1 }}
                        />
                    </Tabs>
                </Box>
                <Stack spacing={2}>
                    <TextField
                        fullWidth
                        size="small"
                        label="Width"
                        type="number"
                        value={design.canvas.breakpoints[activeBreakpoint].width}
                        onChange={handleBreakpointSettingChange('width')}
                        InputProps={{
                            endAdornment: <InputAdornment position="end">px</InputAdornment>
                        }}
                    />
                    <TextField
                        fullWidth
                        size="small"
                        label="Height"
                        type="number"
                        value={design.canvas.breakpoints[activeBreakpoint].height}
                        onChange={handleBreakpointSettingChange('height')}
                        InputProps={{
                            endAdornment: <InputAdornment position="end">px</InputAdornment>
                        }}
                    />
                </Stack>
            </Paper>

            {/* Grid Settings */}
            <Paper 
                elevation={0} 
                sx={{ 
                    p: 2,
                    bgcolor: '#f5f5f5',
                    border: '1px solid',
                    borderColor: '#e0e0e0'
                }}
            >
                <Typography 
                    variant="subtitle2" 
                    gutterBottom
                    sx={{ 
                        color: '#1976d2',
                        fontWeight: 600
                    }}
                >
                    Grid Settings
                </Typography>
                <Stack spacing={2}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={gridSettings.show}
                                onChange={(e) => onGridSettingsUpdate({
                                    ...gridSettings,
                                    show: e.target.checked
                                })}
                                size="small"
                            />
                        }
                        label="Show Grid"
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={gridSettings.snap}
                                onChange={(e) => onGridSettingsUpdate({
                                    ...gridSettings,
                                    snap: e.target.checked
                                })}
                                size="small"
                            />
                        }
                        label="Snap to Grid"
                    />
                    <Box>
                        <Typography variant="caption" color="textSecondary" gutterBottom>
                            Grid Size
                        </Typography>
                        <Slider
                            value={gridSettings.size}
                            onChange={(_, value) => onGridSettingsUpdate({
                                ...gridSettings,
                                size: value as number
                            })}
                            min={5}
                            max={50}
                            step={5}
                            marks={[
                                { value: 5, label: '5px' },
                                { value: 25, label: '25px' },
                                { value: 50, label: '50px' }
                            ]}
                            valueLabelDisplay="auto"
                        />
                    </Box>
                </Stack>
            </Paper>
            <AssetSelectionModal
                open={isAssetModalOpen}
                onClose={() => setIsAssetModalOpen(false)}
                onSelect={handleBackgroundImageSelect}
                assetType="image"
                title="Select Background Image"
            />
        </Stack>
    );
};
